import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, PopupData, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import type { NotificationCreateType } from 'webapp/enums/notifications';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonCollapsiblePillOptionsComponent from 'webapp/components/common/collapsible-pill-options';
import CommonUiIcon from 'webapp/components/common/ui-icon';
import type { ActionListItem } from 'webapp/components/custom-metrics/edit/actions/base';
import NotificationsEditFormComponent from 'webapp/components/notifications/edit/form';
import NotificationsEditListComponent from 'webapp/components/notifications/edit/list';
import NotificationsEditNavigationComponent from 'webapp/components/notifications/edit/navigation';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import type { Notification } from 'webapp/models-custom';
import Alert from 'webapp/models-custom/alert';
import BoardShare from 'webapp/models-custom/board-share';
import MetricAnomaly from 'webapp/models-custom/metric-anomaly';
import Reminder from 'webapp/models-custom/reminder';
import ReportScheduleSetting from 'webapp/models-custom/report-schedule-setting';
import Scorecard from 'webapp/models-custom/scorecard';

import type { ModelWithNotificationForPopup } from 'webapp/services/model-manager/notifications';

const enum Action {
	GET_ASSISTANCE = 'getAssistance',
}

export default class NotificationsEditPopupComponent extends PopupInnerModalComponent<PopupData.Notification.Edit> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	@tracked originalModel: ModelWithNotificationForPopup;
	@tracked model: ModelWithNotificationForPopup;

	// these determine what is the "original state" of popup and which flow led us here
	// used for different logic for "back" flows
	isFromCreateChoicePopupFlow: boolean;
	isFromCreateNewSpecific: boolean;

	@tracked isDuplicateFlow = false; // true only for duplicate actions clicked in-form, and not from the page table itself

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.originalModel = this.popupData.model;
		this.model = this.originalModel;

		this.isFromCreateChoicePopupFlow = this.popupData.isFromCreateChoicePopupFlow;

		this.isFromCreateNewSpecific = !this.isFromCreateChoicePopupFlow && !!this.model.isNew;

		this.args.model.on(PopupEvent.CLOSED, this, () => {
			// cleanup unsaved records
			this.checkToCleanup(this.originalModel);
			this.checkToCleanup(this.model);
		});
	}

	get canDuplicateModel() {
		return this.mmNotificationsService.canDuplicateModel(this.model);
	}

	// metric the edited notification is set up for (metric-based notifications only)
	private get metricForTitle() {
		const model = this.model;
		if (model instanceof Scorecard) {
			// for digest only. regular scorecards can hold multiple metrics
			return model.isDigest ? model.savedDataSelects[0]?.metricSetup.metric : null;
		}
		if (model instanceof Alert || model instanceof MetricAnomaly) {
			return model.metricSetup.metric;
		}
		return null;
	}

	get title() {
		const model = this.model;

		if (model.isNew) {
			return 'New notification';
		}

		if (model instanceof Scorecard && !model.isDigest) {
			return `Edit ‘${model.title}’`;
		}

		if (model instanceof BoardShare) {
			return `Edit scheduled snapshot for ${model.boardObject?.name}`;
		}

		if (model instanceof ReportScheduleSetting) {
			return `Edit scheduled report for ${model.report?.title}`;
		}

		if (model instanceof Reminder) {
			return `Edit reminder for ${model.report?.title}`;
		}

		// metric-based notifications (digest, manual alert, smart alert)
		const metric = this.metricForTitle;
		const metricTitle = `${metric?.name} (${metric?.source?.title})`;

		if (model instanceof MetricAnomaly) {
			return `Edit smart alert for ${metricTitle}`;
		}

		if (model instanceof Alert) {
			return `Edit manual alert for ${metricTitle}`;
		}

		return `Edit notification for ${metricTitle}`;
	}

	// ------ Actions (collapsible-pill) START ------

	private getAssistanceAction: ActionListItem<Action> = {
		icon: 'Assistance',
		text: 'Get assistance',
		action: Action.GET_ASSISTANCE,
	};

	get actionOptions() {
		const options = [];
		if (
			this.model.isNew &&
			!this.currentUserService.isClientUserInPaidClientSpace &&
			!this.isDuplicateFlow
		) {
			options.push(this.getAssistanceAction);
		}
		return options;
	}

	@action
	doAction(actionName?: string) {
		if (actionName) {
			this[actionName as Action]();
		}
	}

	@action
	getAssistance() {
		const trackingData: APIData.Tracking.Data = {
			origin: 'notifications',
		};

		this.analyticsService.event(TrackingEvent.CALL_INITIATED, trackingData);
		this.accountService.requestSetupHelpAlternative();
	}

	// ------ Actions (collapsible-pill) END ------

	@action
	changeTab(type: NotificationCreateType) {
		const oldModel = this.model;

		this.model = this.getNewOfType(type);

		// cleanup unsaved records
		if (oldModel.isNew && oldModel !== this.originalModel) {
			void oldModel.delete(true);
		}

		this.isDuplicateFlow = false;
		this.scheduleReposition();
	}

	@action
	duplicate() {
		const newModel = this.mmNotificationsService.createDuplicatedModel(this.model);
		if (!newModel) {
			return;
		}

		this.originalModel = this.model; // so we can return to it
		this.model = newModel;

		this.isDuplicateFlow = true;

		this.checkToUpdateUrl();
		this.repositionAfterContentChange();
	}

	@action
	updateSubmitInProgress(state: boolean) {
		this.submitInProgress = state;
	}

	@action
	afterSave() {
		this.popupData.onSave?.(this.model);
	}

	// Close after a successful save without triggering the leave-confirm (CLOSE_INTENT).
	// Needed because some save paths (e.g. editing a scorecard you don't own creates a
	// copy) leave the edited model dirty, which would otherwise block the close.
	@action
	forceClose() {
		void this.popupService.close(this.args.model);
	}

	@action
	edit(notification: Notification) {
		const model = notification.targetObject as ModelWithNotificationForPopup;

		const copy = model.createAnotherModel();

		this.model = copy;

		this.scrollFormToTop();

		this.checkToUpdateUrl();
		this.repositionAfterContentChange();
	}

	@action
	deleteFromForm() {
		const notification = this.mmNotificationsService.createFromModel(this.model);
		if (notification) {
			this.delete(notification, true);
		}
	}

	@action
	deleteFromList(notification: Notification) {
		// notification.id is prefixed with the model name, so compare through the target object
		const isEditedNotification =
			notification.targetObject.modelName === this.model.modelName &&
			notification.targetObject.id === this.model.id;
		this.delete(notification, isEditedNotification);
	}

	@action
	delete(notification: Notification, closePopup = false) {
		let newModel: null | ModelWithNotificationForPopup = null;
		if (!closePopup) {
			const type = this.mmNotificationsService.getCreateTypeForModel(
				notification.targetObject,
			);
			if (type) {
				newModel = this.getNewOfType(type);
			}
		}

		this.mmNotificationsService
			.confirmAndDeleteNotification(notification)
			.then(() => {
				if (newModel) {
					this.originalModel = newModel;
					this.model = newModel;
					this.repositionAfterContentChange();
				} else {
					this.close();
				}
			})
			.catch(() => {
				// user rejected, all ok
			});
	}

	private getNewOfType(type: NotificationCreateType) {
		const oldModel = this.model;
		const newModel = this.mmNotificationsService.createOtherCreateTypeModelFromModel(
			type,
			oldModel,
		);
		return newModel;
	}

	private checkToUpdateUrl() {
		const model = this.model;

		if (!this.navigationService.activatedRoute.startsWith('settings.notifications')) {
			return;
		}

		let url = this.navigationService.getPathFromRouteName('settings.notifications');
		if (model && !model.isNew) {
			url = this.navigationService.getPathFromRouteName(
				'settings.notifications.edit',
				this.mmNotificationsService.getCreateTypeForModel(model),
				model.id,
			);
		}

		window.history.replaceState(null, '', url);
	}

	// swapping the model re-renders both the form and the list, and the list only
	// drops the removed row once the delete propagates through the model cache.
	// defer to the next runloop so the new content is in the DOM before we measure.
	private repositionAfterContentChange() {
		next(() => {
			if (!this.isDestroying && !this.isDestroyed) {
				this.scheduleReposition();
			}
		});
	}

	private scrollFormToTop() {
		const popupNode = this.args.model.component?.popupNode;

		if (popupNode) {
			const contentNode = popupNode.querySelector<HTMLElement>('.content');
			if (contentNode) {
				contentNode.scrollTop = 0;
			}
		}
	}

	private checkToCleanup(model: ModelWithNotificationForPopup) {
		if (model.isNew) {
			void model.delete(true);
		}
	}

	<template>
		<div class="title">
			<CommonUiIcon @name="Bell" @size="lg" />
			{{this.title}}

			<div class="action-icons">
				<CommonCollapsiblePillOptionsComponent
					@itemsList={{this.actionOptions}}
					@maxVisibleItems={{3}}
					@showTextProperty="text"
					@doAction={{this.doAction}}
				/>
			</div>
		</div>

		<NotificationsEditNavigationComponent
			@model={{this.model}}
			@isDuplicateFlow={{this.isDuplicateFlow}}
			@isFromCreateNewSpecific={{this.isFromCreateNewSpecific}}
			@changeTab={{this.changeTab}}
		/>

		<div class="content">

			<NotificationsEditFormComponent
				@popup={{@model}}
				@model={{this.model}}
				@submitInProgress={{this.submitInProgress}}
				@updateSubmitInProgress={{this.updateSubmitInProgress}}
				@origin={{this.popupData.origin}}
				@close={{this.close}}
				@duplicate={{if this.canDuplicateModel this.duplicate}}
				@delete={{this.deleteFromForm}}
				@onContentChange={{this.scheduleReposition}}
				@afterSave={{this.afterSave}}
				@forceClose={{this.forceClose}}
			/>

			<NotificationsEditListComponent
				@model={{this.model}}
				@submitInProgress={{this.submitInProgress}}
				@edit={{this.edit}}
				@delete={{this.deleteFromList}}
			/>

		</div>
	</template>
}
